/**
 * OutreachMessageStatusPill — the newest outbound message's state on a row,
 * as an icon badge. Quiet states are icon-only; the attention states keep their
 * label as visible text — a tooltip can't be reached by touch, and these are the
 * ones an operator must not miss.
 *
 * Distinct from OutreachStatusPill, which renders the entry's outreach
 * lifecycle: a row can be in the `sourced` state with a `queued` message on it.
 * `held` is the only status that carries a reason, and it reads "Held · <why>"
 * so staff never have to hover to learn why nothing went out.
 * Every badge is focusable and explains itself in a design-system tooltip. The
 * icon is aria-hidden; the label reaches screen readers as text — visible on
 * attention states, sr-only otherwise. `title` reaches screen readers as text
 * only with announceTitle — the thread card already shows the error as text.
 */
import type { Icon } from '@phosphor-icons/react';
import { CheckCircle, ChatCircle, Clock, PaperPlaneTilt, PauseCircle, Prohibit, Sparkle, WarningCircle, XCircle } from '../../../design-system/icons';
import { cn } from '../../../lib/utils';
import type { OutreachHeldReason, OutreachMessageStatus } from '@discovr/types';
import { FOCUS_RING, StatusTooltip } from './StatusTooltip';

export const HELD_REASON_LABELS: Record<OutreachHeldReason, string> = {
  no_contact: 'no email',
  suppressed: 'do-not-contact',
  entry_not_sendable: 'not sendable',
  mailbox_paused: 'mailbox paused',
  mailbox_disabled: 'mailbox disabled',
  invalid_send_tz: 'invalid time zone',
  daily_cap: 'daily cap',
  recent_contact: 'contacted recently',
  unfilled_placeholder: 'blank to fill in',
  no_mx: 'domain takes no email',
  role_address: 'shared inbox',
  disposable_address: 'disposable address',
  followup_daily_cap: 'follow-up limit',
  no_thread: 'no thread yet',
  no_mailbox: 'no mailbox',
  pacing: 'spaced out',
  recipient_not_allowlisted: 'test inboxes only',
  us_postal_address_missing: 'US address missing',
  send_now_recovery: 'sending now',
  send_hold: 'sending paused',
  campaign_paused: 'campaign paused',
  auto_followups_off: 'follow-ups off',
};

/** A cap deferral keeps the message `queued` (ruling 29), so these reasons also show on a queued badge. */
const QUEUED_REASONS = new Set(['daily_cap', 'followup_daily_cap']);

export function heldReasonLabel(reason: string): string {
  return Object.hasOwn(HELD_REASON_LABELS, reason) ? HELD_REASON_LABELS[reason as OutreachHeldReason] : reason;
}

// The draft mark is Figma's "Help me write" AI sparkle (505:50225): --brand-600 on that panel's wash.
export const STATUS_STYLES: Record<OutreachMessageStatus, { label: string; hint: string; cls: string; icon: Icon }> = {
  awaiting_approval: { label: 'Draft', hint: 'Draft ready', cls: 'bg-linear-to-b from-[#fdfcff] to-[#eeebfb] text-(--brand-600)', icon: Sparkle },
  queued: { label: 'Queued', hint: 'Queued to send', cls: 'bg-[var(--brand-100)] text-[var(--brand-600)]', icon: Clock },
  sending: { label: 'Sending', hint: 'Sending now', cls: 'bg-[var(--brand-100)] text-[var(--brand-600)]', icon: PaperPlaneTilt },
  sent: { label: 'Sent', hint: 'Sent', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  received: { label: 'Reply', hint: 'Creator replied', cls: 'bg-emerald-100 text-emerald-700', icon: ChatCircle },
  held: { label: 'Held', hint: 'Held — not sent', cls: 'bg-amber-100 text-[var(--foreground-warning-default)]', icon: PauseCircle },
  failed: { label: 'Failed', hint: 'Failed to send', cls: 'bg-rose-100 text-rose-700', icon: XCircle },
  unknown_delivery: { label: 'Unknown delivery', hint: "Couldn't confirm delivery", cls: 'bg-rose-100 text-rose-700', icon: WarningCircle },
  cancelled: { label: 'Cancelled', hint: 'Send cancelled', cls: 'bg-stone-200 text-stone-600', icon: Prohibit },
};

const WAITING_EMAIL_LABEL = 'Draft · waiting for email';
const WAITING_EMAIL_HINT = 'Approve it and it goes out once an email is added.';
export const SCHEDULED_WAITING_LABEL = 'Scheduled · waiting for email';

const ATTENTION_STATUSES = new Set<OutreachMessageStatus>(['held', 'failed', 'unknown_delivery']);

const BADGE_BASE = 'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-1';

export interface OutreachMessageStatusPillProps {
  status: OutreachMessageStatus;
  heldReason?: string;
  /** Extra context for the tooltip — typically the message's last error. */
  title?: string;
  /** A more specific meaning than the status's own, e.g. "Scheduled for 9 Oct, 19:45 IST". */
  hint?: string;
  /** Also read `title` to screen readers, where nothing else on screen states it (the row table). */
  announceTitle?: boolean;
  /** Show a quiet state's label as visible text too (a contacted row's delivery status). */
  showLabel?: boolean;
  /** A draft parked without an address (Go draft_waiting_email): it goes out when an email arrives. */
  waitingForEmail?: boolean;
  /** That draft is approved with a stored send choice: it reads Scheduled, never Draft or Sent, until it really goes out. */
  approvedWaiting?: boolean;
}

export function OutreachMessageStatusPill({ status, heldReason, title, hint, announceTitle = false, showLabel = false, waitingForEmail = false, approvedWaiting = false }: OutreachMessageStatusPillProps) {
  const approved = approvedWaiting && status === 'awaiting_approval';
  const style = approved ? STATUS_STYLES.queued : STATUS_STYLES[status];
  if (!style) return <span className={cn(BADGE_BASE, 'bg-stone-200 px-2 text-[11px] font-medium text-stone-600')}>{status}</span>;

  const withReason = !!heldReason && (status === 'held' || (status === 'queued' && QUEUED_REASONS.has(heldReason)));
  const reason = withReason ? heldReasonLabel(heldReason) : null;
  const waiting = waitingForEmail && status === 'awaiting_approval';
  const label = reason ? `${style.label} · ${reason}` : approved ? SCHEDULED_WAITING_LABEL : waiting ? WAITING_EMAIL_LABEL : style.label;
  // A held row passes Go's reason sentence as both reason and title; say it once.
  const detail = title && title !== reason ? title : null;
  const srDetail = announceTitle && detail ? `. ${detail}` : '';
  const StatusIcon = style.icon;
  const meaning = !reason ? (hint ?? (waiting ? WAITING_EMAIL_HINT : style.hint)) : status === 'held' ? label : `${hint ?? style.hint} · ${reason}`;
  const textCls = 'pr-0.5 text-[11px] font-medium';
  return (
    <StatusTooltip content={detail ? `${meaning} — ${detail}` : meaning}>
      <span data-status={status} tabIndex={0} className={cn(BADGE_BASE, FOCUS_RING, style.cls)}>
        <StatusIcon size={14} weight={StatusIcon === Sparkle ? 'regular' : 'bold'} aria-hidden="true" />
        {showLabel || waiting || approved || ATTENTION_STATUSES.has(status) ? (
          <>
            <span className={textCls}>{label}</span>
            {srDetail && <span className="sr-only">{srDetail}</span>}
          </>
        ) : (
          <>
            {reason && (
              <span aria-hidden="true" className={textCls}>
                {reason}
              </span>
            )}
            <span className="sr-only">{label + srDetail}</span>
          </>
        )}
      </span>
    </StatusTooltip>
  );
}
